import * as React from 'react';
import classnames from 'classnames';
import styles from './main.scss';
import debounce from 'lodash.debounce';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';
type ImageTextOverlayProps = {
    imagePath: string;
    imageAlt?: string;
    imageClass?: string;
    overlayClass?: string;
    message: React.ReactNode;
    messageSize?: 'default' | 'small';
    onImageLoad?: () => void;
    showOverlay: boolean;
};

type ImageTextOverlayState = {
    overlayWidth: number;
    overlayHeight: number;
    imageLoaded: boolean;
};

class ImageTextOverlay extends React.Component<ImageTextOverlayProps, ImageTextOverlayState> {
    static defaultProps = {
        messageSize: 'default',
    };

    constructor(props: ImageTextOverlayProps) {
        super(props);
        this.state = {
            overlayWidth: 0,
            overlayHeight: 0,
            imageLoaded: false,
        };
        this.onImageLoaded = this.onImageLoaded.bind(this);
        this.resizeOverlay = debounce(this.resizeOverlay.bind(this), 100, { leading: true });
        this.imageRef = React.createRef();
    }

    componentDidMount(): void {
        window.addEventListener('resize', this.resizeOverlay);
    }

    componentDidUpdate(prevProps: ImageTextOverlayProps, prevState: ImageTextOverlayState): void {
        // Call onImageLoad function once, after DOM is updated to reflect the loaded state
        if (!prevState.imageLoaded && this.state.imageLoaded && this.props.onImageLoad) {
            this.props.onImageLoad();
        }
    }

    componentWillUnmount(): void {
        window.removeEventListener('resize', this.resizeOverlay);
    }

    imageRef: React.RefObject<HTMLImageElement>;

    resizeOverlay(): void {
        if (this.props.showOverlay && this.imageRef.current) {
            const { width, height } = this.imageRef.current.getBoundingClientRect();
            this.setState({
                overlayWidth: width,
                overlayHeight: height,
            });
        }
    }

    onImageLoaded(): void {
        this.setState({
            imageLoaded: true,
        });
        this.resizeOverlay();
    }

    render(): React.ReactNode {
        const { imagePath, imageAlt, message, messageSize, showOverlay, imageClass, overlayClass } =
            this.props;
        const imageClassValue = imageClass || '';
        const overlayClassValue = overlayClass || '';
        const { overlayWidth, overlayHeight, imageLoaded } = this.state;
        return (
            <>
                <img
                    data-tn="item-overlay-image"
                    ref={this.imageRef}
                    className={classnames(styles.image, {
                        [imageClassValue]: !!imageClass,
                        [styles.isHidden]: showOverlay && (!imageLoaded || !overlayWidth),
                    })}
                    src={imagePath}
                    alt={imageAlt}
                    onLoad={this.onImageLoaded}
                />
                {showOverlay && imageLoaded && overlayWidth !== 0 && (
                    <div
                        data-tn="overlay-visible"
                        className={classnames(styles.imageOverlay, {
                            [overlayClassValue]: !!overlayClass,
                        })}
                        style={{ width: overlayWidth, height: overlayHeight }}
                    >
                        <span
                            className={classnames(styles.message, {
                                [styles.messageSmall]: messageSize === 'small',
                            })}
                        >
                            {message}
                        </span>
                    </div>
                )}
                <VisibilityTracker
                    onVisibilityChange={({ isVisible }) => {
                        if (isVisible) {
                            this.resizeOverlay();
                        }
                    }}
                    elementRef={this.imageRef}
                />
            </>
        );
    }
}

export { ImageTextOverlay };
